/**
 * AI Processing Indicator Component
 * Engaging animation showing AI at work
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SparklesIcon } from '@heroicons/react/24/outline'

interface AIProcessingIndicatorProps {
  isVisible: boolean
  taskType?: string
}

const AIProcessingIndicator: React.FC<AIProcessingIndicatorProps> = ({
  isVisible,
  taskType = 'task',
}) => {
  // Always render AnimatePresence, but conditionally show content

  const taskLabels: Record<string, string> = {
    proofread: 'Proofreading',
    rewrite: 'Rewriting',
    expand: 'Expanding',
    summarize: 'Summarizing',
    explain: 'Explaining',
  }

  const label = taskLabels[taskType] || 'Processing'

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.9 }}
          className="fixed top-4 right-4 z-[100] bg-gradient-to-r from-blue-700 to-blue-800 dark:from-gray-700 dark:to-gray-600 rounded-lg shadow-2xl p-4 min-w-[200px]"
        >
          <div className="flex items-center gap-3">
            {/* Animated Sparkles */}
            <div className="relative">
              <motion.div
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <SparklesIcon className="w-6 h-6 text-white" />
              </motion.div>
              
              {/* Pulsing dots */}
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-full bg-white"
                  animate={{
                    scale: [0, 1.5, 0],
                    opacity: [0.8, 0, 0],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: 'easeOut',
                  }}
                  style={{
                    width: '8px',
                    height: '8px',
                    left: '50%',
                    top: '50%',
                    marginLeft: '-4px',
                    marginTop: '-4px',
                  }}
                />
              ))}
            </div>

            {/* Text */}
            <div className="flex-1">
              <motion.div
                animate={{
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="text-white font-semibold text-sm"
              >
                {label}...
              </motion.div>
              <div className="text-white/80 text-xs mt-0.5">
                AI is working
              </div>
            </div>

            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 rounded-b-lg overflow-hidden">
              <motion.div
                className="h-full bg-white"
                animate={{
                  x: ['-100%', '100%'],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear',
                }}
                style={{
                  width: '50%',
                }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default AIProcessingIndicator

